import { Helmet } from "react-helmet-async";
import { COMPANY_INFO } from "@/lib/brandConfig";
import { DFW_CITIES } from "@/lib/dfwCities";

interface LocalBusinessSchemaProps {
  type?: "LocalBusiness" | "Store" | "HomeAndConstructionBusiness" | "FurnitureStore";
  name: string;
  description: string;
  url: string;
  image?: string;
}

export const LocalBusinessSchema = ({
  type = "Store",
  name,
  description,
  url,
  image,
}: LocalBusinessSchemaProps) => {
  const serviceAreaCities = DFW_CITIES.map((city) => ({
    "@type": "City",
    name: city.name,
    containedInPlace: {
      "@type": "State",
      name: "Texas",
    },
  }));

  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${url}#organization`,
    name: name,
    description: description,
    url: url,
    image: image,
    telephone: COMPANY_INFO.phone,
    email: COMPANY_INFO.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY_INFO.address.street,
      addressLocality: COMPANY_INFO.address.city,
      addressRegion: COMPANY_INFO.address.state,
      postalCode: COMPANY_INFO.address.zip,
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 32.9756,
      longitude: -96.8897,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Friday",
        opens: "10:00",
        closes: "17:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "10:00",
        closes: "17:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "10:30",
        closes: "16:00",
      },
    ],
    areaServed: serviceAreaCities,
    sameAs: [
      "https://www.facebook.com/bbqislandliquidators",
      "https://www.instagram.com/bbqislandliquidators",
      "https://www.youtube.com/@bbqislandliquidators",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Products",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Free Customer Pickup",
          },
        },
      ],
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Website Schema for brand sites
interface WebsiteSchemaProps {
  name: string;
  url: string;
  description: string;
}

export const WebsiteSchema = ({ name, url, description }: WebsiteSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${url}#website`,
    name: name,
    url: url,
    description: description,
    publisher: {
      "@type": "Organization",
      name: name,
      url: url,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: `${url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Breadcrumb Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Article/Blog Schema (without pricing)
interface ArticleSchemaProps {
  headline: string;
  description: string;
  image?: string;
  author: string;
  publishedDate: string;
  modifiedDate?: string;
  url: string;
  publisher: string;
  publisherUrl: string;
}

export const ArticleSchema = ({
  headline,
  description,
  image,
  author,
  publishedDate,
  modifiedDate,
  url,
  publisher,
  publisherUrl,
}: ArticleSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: headline,
    description: description,
    image: image,
    author: {
      "@type": "Person",
      name: author,
    },
    publisher: {
      "@type": "Organization",
      name: publisher,
      url: publisherUrl,
    },
    datePublished: publishedDate,
    dateModified: modifiedDate || publishedDate,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// FAQ Schema
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  items: FAQItem[];
}

export const FAQSchema = ({ items }: FAQSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Organization Schema
interface OrganizationSchemaProps {
  name: string;
  url: string;
  logo?: string;
  description: string;
}

export const OrganizationSchema = ({ name, url, logo, description }: OrganizationSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${url}#organization`,
    name: name,
    url: url,
    logo: logo,
    description: description,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: COMPANY_INFO.phone,
      contactType: "sales",
      areaServed: "US",
      availableLanguage: "English",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY_INFO.address.street,
      addressLocality: COMPANY_INFO.address.city,
      addressRegion: COMPANY_INFO.address.state,
      postalCode: COMPANY_INFO.address.zip,
      addressCountry: "US",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};